import { formatInTimeZone, toZonedTime } from 'date-fns-tz'

/**
 * Duration, time-zone and money rules, kept pure and free of Supabase so they
 * can be unit-tested and so the client agrees with the SQL in entry_billing
 * by construction rather than by coincidence.
 */

export const DEFAULT_TZ = 'Asia/Singapore'

export type Instant = string | Date

const asDate = (value: Instant): Date => (value instanceof Date ? value : new Date(value))

/* ------------------------------------------------------------------ */
/* Duration                                                            */
/* ------------------------------------------------------------------ */

/** Whole seconds between two instants. Null while a timer is still running. */
export function durationSeconds(startedAt: Instant, endedAt: Instant | null): number | null {
  if (endedAt === null) return null
  const ms = asDate(endedAt).getTime() - asDate(startedAt).getTime()
  if (!Number.isFinite(ms)) return null
  return Math.max(0, Math.floor(ms / 1000))
}

/**
 * Hours to 4dp — the same precision entry_billing uses, so a total computed
 * in the browser and one computed in SQL do not drift apart.
 */
export function durationHours(startedAt: Instant, endedAt: Instant | null): number | null {
  const seconds = durationSeconds(startedAt, endedAt)
  if (seconds === null) return null
  return round(seconds / 3600, 4)
}


/** `7.25` hours, the convention used in reports and the CSV. */
export function formatDecimalHours(totalSeconds: number): string {
  return round(Math.max(0, totalSeconds) / 3600, 2).toFixed(2)
}

/* ------------------------------------------------------------------ */
/* Time zones                                                          */
/* ------------------------------------------------------------------ */

/**
 * The calendar date an instant falls on in a given zone.
 *
 * This is the join key for rate effectivity. Doing it with the browser's own
 * zone would put an 8am Singapore entry on the previous day for anyone
 * viewing from London, and bill it at the wrong rate.
 */
export function localDate(instant: Instant, timeZone: string = DEFAULT_TZ): string {
  return formatInTimeZone(asDate(instant), timeZone, 'yyyy-MM-dd')
}

export function localTime(
  instant: Instant,
  timeZone: string = DEFAULT_TZ,
  use24Hour = true,
): string {
  return formatInTimeZone(asDate(instant), timeZone, use24Hour ? 'HH:mm' : 'h:mm a')
}

/** A Date shifted so its local getters read as wall-clock time in `timeZone`. */
export function inZone(instant: Instant, timeZone: string = DEFAULT_TZ): Date {
  return toZonedTime(asDate(instant), timeZone)
}

/* ------------------------------------------------------------------ */
/* Overlap (C5)                                                        */
/* ------------------------------------------------------------------ */

export interface Span {
  id?: string
  started_at: string
  ended_at: string | null
}

/**
 * Half-open [start, end): an entry ending at 10:00 and one starting at 10:00
 * do not overlap. Matches the tstzrange '[)' the database validates with.
 */
export function spansOverlap(a: Span, b: Span): boolean {
  if (a.ended_at === null || b.ended_at === null) return false

  const aStart = asDate(a.started_at).getTime()
  const aEnd = asDate(a.ended_at).getTime()
  const bStart = asDate(b.started_at).getTime()
  const bEnd = asDate(b.ended_at).getTime()

  return aStart < bEnd && bStart < aEnd
}

/**
 * The ids of entries that share clock time with another of the same person's.
 *
 * Overlapping is ALLOWED -- a person may deliberately log concurrent work, and
 * neither the database nor the app refuses it. What used to live here was a
 * validateEntry() that rejected overlaps; it mirrored a SQL rule that
 * 20260925091000 dropped when it rewrote validate_time_entry(), and nothing
 * ever called it. Enforcing a rule that does not exist is worse than not
 * having one, so this marks instead of refusing: a duplicate logged by
 * accident is visible, and time logged concurrently on purpose is untouched.
 *
 * Overlapping time is summed, never deduplicated, so every marked pair is
 * billed twice over. That is the point of the marker.
 *
 * Half-open, exactly as spansOverlap and the database's tstzrange '[)' are:
 * an entry ending at 10:00 and one starting at 10:00 do not overlap. A sorted
 * sweep rather than every pair, because this runs over the whole visible list
 * on each render.
 */
export interface DayEntry {
  id: string
  /** `YYYY-MM-DD`. */
  day: string
  /** Seconds since local midnight. */
  start: number
  /** Seconds. Zero or less means nothing to compare -- a running entry. */
  dur: number
}

export function overlappingIds(entries: readonly DayEntry[]): Set<string> {
  const found = new Set<string>()

  const items = entries
    .filter((e) => e.dur > 0)
    .map((e) => {
      // Parsed as UTC for every entry alike. Only differences matter here, so
      // the absolute zone is irrelevant and this cannot drift between rows.
      const base = Date.parse(`${e.day}T00:00:00Z`) / 1000
      return { id: e.id, from: base + e.start, to: base + e.start + e.dur }
    })
    .filter((it) => Number.isFinite(it.from) && Number.isFinite(it.to))
    .sort((a, b) => a.from - b.from)

  let active: typeof items = []
  for (const it of items) {
    // `> it.from`, not `>=`: touching at an instant is not overlapping.
    active = active.filter((a) => a.to > it.from)
    if (active.length > 0) {
      found.add(it.id)
      for (const a of active) found.add(a.id)
    }
    active.push(it)
  }

  return found
}

/* ------------------------------------------------------------------ */
/* Money                                                               */
/* ------------------------------------------------------------------ */

/** Half-up to `dp`, nudged past the float representation of .005 cases. */
export function round(value: number, dp = 2): number {
  if (!Number.isFinite(value)) return 0
  const factor = 10 ** dp
  return Math.round((value + Number.EPSILON) * factor) / factor
}

export const roundMoney = (value: number): number => round(value, 2)

export interface EffectiveRate {
  hourly_rate: number
  currency: string
  effective_from: string
}

/**
 * The rate in force on a date: the latest one starting on or before it.
 * Null when the person had no rate yet, which reports as "No rate set"
 * rather than being silently billed at zero.
 */
export function rateOnDate(
  rates: readonly EffectiveRate[],
  date: string,
): EffectiveRate | null {
  let best: EffectiveRate | null = null
  for (const rate of rates) {
    if (rate.effective_from > date) continue
    if (!best || rate.effective_from > best.effective_from) best = rate
  }
  return best
}

export type PricingModel =
  | 'running'
  | 'non_billable'
  | 'task_fixed'
  | 'project_fixed'
  | 'user_hourly'
  | 'project_hourly'
  | 'no_rate'

/**
 * The pricing models that mean "a flat amount, not hours times a rate".
 *
 * Named once, here, because two places need the same answer: the Reports
 * filter on screen and the CSV the same filter exports. A list that drifted
 * between them would be a file that disagrees with the report it came from.
 *
 * All three, including project_fixed. The "fixed fee" TAG on a breakdown row
 * deliberately leaves that one out -- a project's flat price is counted once
 * at the project and never belongs to a single entry, so labelling the entry
 * would double it in the reader's head. The filter is answering a different
 * question: which work is on a fixed fee at all. project_fixed is.
 *
 * `entry_fixed` is not in the PricingModel union above, which describes what
 * the client can compute; the database's view emits it for an entry carrying
 * its own fixed_amount. Typed as the strings the view produces.
 */
export const FIXED_FEE_MODELS = ['entry_fixed', 'task_fixed', 'project_fixed'] as const

export interface PricingInputs {
  durationHours: number | null
  isBillable: boolean
  taskFixedPrice?: number | null
  projectFixedPrice?: number | null
  userHourlyRate?: number | null
  projectHourlyRate?: number | null
}

/** Mirrors the CASE in entry_billing. Keep the two in step. */
export function pricingModel(input: PricingInputs): PricingModel {
  if (input.durationHours === null) return 'running'
  if (!input.isBillable) return 'non_billable'
  if (input.taskFixedPrice != null) return 'task_fixed'
  if (input.projectFixedPrice != null) return 'project_fixed'
  if (input.userHourlyRate != null) return 'user_hourly'
  if (input.projectHourlyRate) return 'project_hourly'
  return 'no_rate'
}

/**
 * What one entry bills.
 *
 * Null means "no figure belongs on this row" — either the timer is running,
 * the person has no rate, or the project is billed at a flat price that is
 * reported once rather than smeared across the entries under it.
 */
export function entryAmount(input: PricingInputs): number | null {
  const model = pricingModel(input)
  const hours = input.durationHours ?? 0

  switch (model) {
    case 'running':
      return null
    case 'non_billable':
      return 0
    case 'task_fixed':
      return roundMoney(input.taskFixedPrice as number)
    case 'project_fixed':
      return null
    case 'user_hourly':
      return roundMoney(hours * (input.userHourlyRate as number))
    case 'project_hourly':
      return roundMoney(hours * (input.projectHourlyRate as number))
    default:
      return null
  }
}

/**
 * `S$1,234.50`.
 *
 * Intl's narrowSymbol renders SGD as a bare "$" in most locales, which is
 * indistinguishable from US dollars on an invoice. The prefix is chosen
 * explicitly, and anything unrecognised falls back to the ISO code rather
 * than a symbol that could mean three different currencies.
 */
const CURRENCY_PREFIX: Record<string, string> = {
  SGD: 'S$',
  USD: 'US$',
  AUD: 'A$',
  NZD: 'NZ$',
  HKD: 'HK$',
  CAD: 'C$',
  GBP: '£',
  EUR: '€',
  JPY: '¥',
  MYR: 'RM',
}

/**
 * Whether an entry actually charges, as the screens should read it.
 *
 * Three flags, ANDed, mirroring entry_billing exactly: the entry's own, the
 * project's, and the person's. Any one of them false means the time is worth
 * nothing. The entry's flag alone is not the answer -- a project or a person
 * marked as not charging overrides it.
 *
 * Absent means billable, for both the project and the member. That is the
 * load-bearing half: an entry with NO project must fall through to its own
 * flag rather than being forced non-billable. The database used to say
 * `coalesce(p.is_billable, false)` here, so time logged against no project
 * priced at the full rate on screen and exported as 0.00 in both CSVs --
 * the screen and the invoice disagreed about the same eight hours.
 *
 * Every parameter is required so that adding a flag later cannot silently
 * leave a call site on the old rule; the compiler names each one instead.
 */
export function chargesForTime(
  entryBillable: boolean,
  memberBillable: boolean | null | undefined,
  projectBillable: boolean | null | undefined,
): boolean {
  return entryBillable && (projectBillable ?? true) && (memberBillable ?? true)
}

/**
 * The currency to label a workspace's figures with.
 *
 * Charge-out rates carry their own currency and nothing forces them to agree,
 * so this takes the first and falls back to USD. It is an approximation in a
 * workspace that genuinely mixes currencies; it exists so that the
 * approximation is made once rather than re-invented, or replaced by a bare
 * "$", per screen.
 *
 * entry_billing's own coalesce chain still ends in SGD, so the two disagree
 * where NEITHER has anything to go on -- a workspace with no rates at all.
 * That case prices nothing: the model is 'no_rate' and the amount is null, so
 * the label hangs off a figure that does not exist. Worth knowing before
 * somebody reads the mismatch as a bug.
 */
export function workspaceCurrency(rates: readonly { currency: string }[]): string {
  return rates[0]?.currency ?? 'USD'
}

/**
 * An amount a person typed into a money field, or null if they typed nothing.
 *
 * Null and zero are different answers and the caller acts on both: null means
 * "no amount given", zero means "charge nothing". Anything unreadable is null
 * rather than zero, because silently charging nothing is the worst available
 * reading of a typo.
 *
 * The character class is spelled 0-9 rather than with an escape on purpose.
 * This function was first written inline with a backslash in it, the backslash
 * was lost on the way into the file, and the class became "anything that is
 * not the letter d" -- which stripped every digit, turned "2000" into "", and
 * saved a two-thousand-dollar fee as zero.
 */
export function parseMoneyInput(value: string): number | null {
  const t = String(value).trim()
  if (!t) return null

  const cleaned = t.replace(/[^0-9.]/g, '')
  if (!cleaned || cleaned === '.') return null

  const n = Number(cleaned)
  if (!Number.isFinite(n)) return null
  return Math.max(0, n)
}

export function formatMoney(amount: number | null | undefined, currency = 'USD'): string {
  if (amount === null || amount === undefined || !Number.isFinite(amount)) return '—'

  const code = currency.toUpperCase()
  const digits = new Intl.NumberFormat('en-SG', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)

  const prefix = CURRENCY_PREFIX[code]
  return prefix ? `${prefix}${digits}` : `${code} ${digits}`
}

export interface BillingTotals {
  hours: number
  billableHours: number
  amount: number
  unratedEntries: number
}

/** Running entries contribute nothing at all (R3). */
export function sumBilling(
  rows: readonly (PricingInputs & { amount?: number | null })[],
): BillingTotals {
  const totals: BillingTotals = { hours: 0, billableHours: 0, amount: 0, unratedEntries: 0 }

  for (const row of rows) {
    if (row.durationHours === null) continue

    totals.hours += row.durationHours
    if (row.isBillable) totals.billableHours += row.durationHours

    const model = pricingModel(row)
    if (model === 'no_rate') totals.unratedEntries += 1

    const amount = row.amount !== undefined ? row.amount : entryAmount(row)
    if (typeof amount === 'number') totals.amount += amount
  }

  totals.hours = round(totals.hours, 4)
  totals.billableHours = round(totals.billableHours, 4)
  totals.amount = roundMoney(totals.amount)
  return totals
}
